import { AbstractControl, ValidationErrors, ValidatorFn } from '@angular/forms'

/** '50,000' / ' 2000 ' -> 50000 / 2000; null when empty, NaN when it isn't a number. The backend strips commas too. */
export function parseAmount(value: unknown): number | null {
    if (value === null || value === undefined) return null
    const text = String(value).replace(/[,\s]/g, '')
    if (!text) return null
    return /^\d+(\.\d+)?$/.test(text) ? Number(text) : NaN
}

/** A naira amount, commas allowed, at least `min`. Empty passes - pair it with Validators.required. */
export function amountValidator(min = 1): ValidatorFn {
    return (control: AbstractControl): ValidationErrors | null => {
        const amount = parseAmount(control.value)
        if (amount === null) return null
        if (Number.isNaN(amount)) return { notANumber: true }
        return amount < min ? { min: { min, actual: amount } } : null
    }
}

/** At most the amount in the sibling control `other` (a negotiable item's lowest price, at most its price). */
export function notMoreThanValidator(other: string): ValidatorFn {
    return (control: AbstractControl): ValidationErrors | null => {
        const amount = parseAmount(control.value)
        const max = parseAmount(control.parent?.get(other)?.value)
        if (amount === null || max === null || Number.isNaN(amount) || Number.isNaN(max)) return null
        return amount > max ? { notMoreThan: { max } } : null
    }
}

/** A Nigerian phone number: 0801 234 5678, +234 801 234 5678, 0801-234-5678. Empty passes. */
export function phoneNumberValidator(control: AbstractControl): ValidationErrors | null {
    const value = String(control.value ?? '').trim()
    if (!value) return null
    const digits = value.replace(/\D/g, '')
    const valid = /^\+?[\d\s\-()]+$/.test(value) && digits.length >= 10 && digits.length <= 14
    return valid ? null : { phoneNumber: true }
}

/** Form fields Angular marks invalid, and anything marked `data-invalid="true"` (e.g. a missing photo). */
const INVALID_FIELDS = [
    'input.ng-invalid',
    'textarea.ng-invalid',
    'mat-select.ng-invalid',
    'mat-radio-group.ng-invalid',
    'mat-button-toggle-group.ng-invalid',
    '[data-invalid="true"]',
].join(', ')

/**
 * Scrolls to the first field with an error inside `root` and focuses it (or the first focusable thing in it - a
 * radio group's first option), so nobody is left wondering what stopped them. Returns whether it found one.
 */
export function focusFirstInvalid(root: HTMLElement | null | undefined): boolean {
    const field = root?.querySelector<HTMLElement>(INVALID_FIELDS)
    if (!field) return false
    const focusable = field.matches('input, textarea, [tabindex]')
        ? field
        : field.querySelector<HTMLElement>('input, textarea, button, [tabindex]:not([tabindex="-1"])')
    field.scrollIntoView?.({ block: 'center', behavior: 'smooth' })
    focusable?.focus({ preventScroll: true })
    return true
}
